import { getSandboxUrl } from "sj-ui-toolkit/src/client/sandboxUrl";

import { setHeaderValue, encodeToBase64, decodeFromBase64, replLanguageMap } from "../util";
import { DirectoryListing, DirectoryItem, FileInfo, FileWrite, FsInfo } from "./fsTypes";

const CfgIncludeCredentials = { credentials: "include" } as RequestInit;

const FsApiUri = getSandboxUrl() + "/svc/sandbox/fs-api/fs";

/**
 * Client for the sandbox filesystem API (see svc-sandbox/fs) which provides user-facing
 * apps with a means of performing basic file I/O operations against a sandbox.
 */
export class SandboxFilesystem {
  private activeVolume: string | null = null;
  private volumeNames: string[] = [];
  abortController: AbortController | null;

  constructor() {
    this.abortController = null;
  }

  /**
   * Initializes the filesystem client by fetching the basic filesystem info from the
   * sandbox, and setting up the requested repl volume as the active volume for future
   * file operations.
   */
  async init(language: string): Promise<void> {
    const replVolume = replLanguageMap(language);

    // Make an API request to get the basic details of the sandbox's filesystem
    let info = undefined as FsInfo | undefined;
    const requestInit = this.withAbort();
    const r = await fetch(FsApiUri, requestInit);

    checkResponse(r, "fetch filesystem info");
    info = await readFsInfo(r);

    // Update our list of available volume names, so clients know which volumes they can
    // fetch directory listings and files from
    this.volumeNames = info.volumes.map(x => x.name);

    // If the requested repl volume is available, set it as the active volume
    if (this.volumeNames.includes(replVolume)) {
      this.activeVolume = replVolume;
    } else {
      throw new Error(`requested REPL volume ${replVolume} is not available`);
    }
  }

  // abort pending request and set controller to an initial state
  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Makes an API call to get a recursive listing of all files and directories contained
   * in the root of the requested volume. If no such volume exists on the sandbox or if
   * the request otherwise fails, reports an error via the onError callback and returns
   * null.
   */
  async fetchDirectoryListing(): Promise<DirectoryItem[] | null> {
    this.ensureActiveVolume();

    const r = await fetch(`${FsApiUri}/v/${this.activeVolume}`, CfgIncludeCredentials);

    checkResponse(r, `fetch directory listing for volume ${this.activeVolume}`);
    const listing = await readDirectoryListing(r);
    return listing.directory;
  }

  /**
   * Makes an API call to request the full contents of a file, returned as a plain-text
   * string. If the given volume doesn't exist, or if the given path doesn't correspond
   * to a readable file on that volume, or if the request otherwise fails, reports an
   * error via the onError callback and returns null.
   */
  async fetchFileContents(path: string): Promise<string | null> {
    this.ensureActiveVolume();

    const r = await fetch(`${FsApiUri}/v/${this.activeVolume}/${path}`, CfgIncludeCredentials);

    checkResponse(r, `fetch file contents for volume ${this.activeVolume}, path ${path}`);
    const info = await readFileInfo(r);
    return info.contents;
  }

  /**
   * Makes an API call to update the file at the given path on the specified volume,
   * returning true on success. If the given volume doesn't exist, or if the given path
   * doesn't correspond to a writeable file on that volume, or if the request otherwise
   * fails, reports an error via the onError callback and returns false.
   */
  async writeFileContents(path: string, newContents: string): Promise<boolean> {
    this.ensureActiveVolume();

    // Base64-encode the latest user-edited contents for the file and craft a FileWrite
    // payload to supply to our API request
    const payload: FileWrite = {
      contents: encodeToBase64(newContents),
    };

    // Make an API request to write the new contents to the file
    const init = {
      ...CfgIncludeCredentials,
      method: "put",
      body: JSON.stringify(payload),
    };
    setHeaderValue(init, "Content-Type", "application/json");
    const r = await fetch(`${FsApiUri}/v/${this.activeVolume}/${path}`, init);
    checkResponse(r, `write file contents for volume ${this.activeVolume}, path ${path}`);
    return true;
  }

  /** Returns the names of all available volumes on the sandbox's filesystem. */
  getVolumeNames(): string[] {
    return this.volumeNames;
  }

  /**
   * Prepares a RequestInit object that will ensure that a request can be canceled
   * mid-flight by calling the resulting abort function.
   */
  protected withAbort(): RequestInit {
    this.abortController = new AbortController();
    const init: RequestInit = {
      ...CfgIncludeCredentials,
      signal: this.abortController.signal,
    };

    return init;
  }

  /** Throws an error if no active volume is set */
  protected ensureActiveVolume(): void {
    if (!this.activeVolume) {
      throw new Error("no active volume set");
    }
  }
}

/**
 * Throws an Error if the response's status is not 200-level; otherwise does nothing.
 */
function checkResponse(r: Response, desc: string): void {
  if (!r.ok) {
    const status = r ? r.status : -1;
    const message = `failed to ${desc}: got status ${status}`;
    throw new Error(message);
  }
}

/**
 * Reads a JSON response body and parses it to a valid FsInfo, throwing an Error if the
 * response does not contain a valid JSON-encoded FsInfo.
 */
async function readFsInfo(r: Response): Promise<FsInfo> {
  const data = await r.json();
  if (typeof data !== "object") {
    throw new Error("response data is not an object");
  }
  const volumesArray = data["volumes"];
  if (!Array.isArray(volumesArray)) {
    throw new Error("missing 'volumes' array");
  }
  const volumes = [];
  for (const obj of volumesArray) {
    if (typeof obj !== "object") {
      throw new Error("item in 'volumes' array is not an object");
    }
    const name = obj["name"];
    if (typeof name !== "string") {
      throw new Error("item in 'volumes' array is missing string 'name' field");
    }
    volumes.push({ name });
  }
  return { volumes };
}

/**
 * Reads a JSON response body and parses it to a valid DirectoryListing, throwing an
 * Error if the response does not contain a valid JSON-encoded DirectoryListing.
 */
async function readDirectoryListing(r: Response): Promise<DirectoryListing> {
  const data = await r.json();
  if (typeof data !== "object") {
    throw new Error("response data is not an object");
  }
  const path = data["path"];
  if (typeof path !== "string") {
    throw new Error("missing string 'path' field");
  }
  const directory = data["directory"];
  if (!Array.isArray(directory)) {
    throw new Error("missing 'directory' array");
  }
  return { path, directory };
}

/**
 * Reads a JSON response body and parses it to a valid FileInfo, throwing an Error if
 * the response does not contain a valid JSON-encoded FileInfo.
 */
async function readFileInfo(r: Response): Promise<FileInfo> {
  const data = await r.json();
  if (typeof data !== "object") {
    throw new Error("response data is not an object");
  }
  const path = data["path"];
  if (typeof path !== "string") {
    throw new Error("missing string 'path' field");
  }
  const mtime = data["mtime"];
  if (typeof mtime !== "number") {
    throw new Error("missing numeric 'mtime' field");
  }
  const contentsBase64 = data["contents"];
  if (typeof contentsBase64 !== "string") {
    throw new Error("missing string 'contents' field");
  }
  const contents = decodeFromBase64(contentsBase64);
  return { path, mtime, contents };
}
